import compact from "lodash/compact";
import map from "lodash/map";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { SelectV2 } from "PFComponents/select_v2";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import {
  fetchSkillsFrameworksOptions,
  SkillsFrameworksOptionsResponse
} from "PFCore/services/skills_frameworks/fetch_skills_frameworks_options";
import { Profile, SkillsFramework, SkillsFrameworkRank, Subtemplate, Template } from "PFCore/types";
import { FetchOptionsResponse, Option } from "PFTypes/options";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConflictsReviewSidePanel } from "./conflicts_review_side_panel";
import { useCanApplySkillsFramework } from "./hooks/use_can_apply_skills_framework";
import { useConflictResolver } from "./hooks/use_conflict_resolver";
import { usePopulateSkillsFrameworks } from "./hooks/use_populate_skills_frameworks";
import { AttributesBox } from "./parts/attributes_box";

type SkillsFrameworksSidePanelProps = {
  selectedSkillsFrameworks: SkillsFramework[];
  template: Template;
  subtemplate: Subtemplate | null;
  activityCreator: Profile | null;
  roleFormKeyPrefix?: string;
  onClose: VoidFunction;
  closeOnClickOutside?: boolean;
  onFrameworksChange: (selectedFrameworks: SkillsFramework[]) => void;
  onPostPopulate?: (data: FormFieldData[]) => void;
};

const formatOptions = (response: SkillsFrameworksOptionsResponse): FetchOptionsResponse<SkillsFramework> => ({
  ...response,
  entries: response.entries.map((skillsFramework) => ({
    id: skillsFramework.id,
    value: skillsFramework.name,
    displayElement: skillsFramework.name,
    original: skillsFramework
  }))
});

export const SkillsFrameworksSidePanel = ({
  selectedSkillsFrameworks,
  template,
  subtemplate,
  activityCreator,
  roleFormKeyPrefix,
  closeOnClickOutside,
  onClose,
  onFrameworksChange,
  onPostPopulate
}: SkillsFrameworksSidePanelProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });
  const { t: tTranslation } = useTranslation();
  const { populateSkillsFrameworksValues } = usePopulateSkillsFrameworks({
    template,
    subtemplate,
    activityCreator,
    roleFormKeyPrefix,
    onPostPopulate
  });

  const canApplyChanges = useCanApplySkillsFramework({ activityCreator });

  const [primaryFramework, setPrimaryFramework] = useState<SkillsFramework | undefined>(
    selectedSkillsFrameworks.find(({ rank }) => rank === SkillsFrameworkRank.Primary)
  );
  const [secondaryFrameworks, setSecondaryFrameworks] = useState<SkillsFramework[]>(
    selectedSkillsFrameworks.filter(({ rank }) => rank === SkillsFrameworkRank.Secondary)
  );
  const [isConflictsSidePanelOpen, setConflictsSidePanelOpen] = useState(false);

  const selectedFrameworks = useMemo(
    () => compact([primaryFramework, ...secondaryFrameworks]),
    [primaryFramework, secondaryFrameworks]
  );

  const { conflicts, resolvedAttributes, handleConflictsResolve } = useConflictResolver({
    selectedFrameworks,
    template,
    subtemplate
  });

  const handleSubmit = () => {
    onFrameworksChange(selectedFrameworks);
    populateSkillsFrameworksValues(resolvedAttributes.skills, resolvedAttributes.customFields);
  };

  const footerRenderer = ({ onSidePanelClose }) => (
    <Inline justifyContent="space-between">
      <Button kind="secondary" onClick={onSidePanelClose}>
        {tTranslation("cancel")}
      </Button>
      <Button
        onClick={() => {
          handleSubmit();
          onSidePanelClose();
        }}
        disabled={selectedFrameworks.length === 0 || !canApplyChanges(resolvedAttributes.customFields)}
      >
        {t("apply")}
      </Button>
    </Inline>
  );

  return (
    <SidePanel
      fullHeight
      show
      closeOnClickOutside={closeOnClickOutside}
      title={t("addSkillsFrameworks")}
      footerRenderer={footerRenderer}
      onClose={onClose}
    >
      <Stack gap="spacingLg">
        <SelectV2<SkillsFramework>
          label={t("sidePanel.primarySkillsFramework")}
          fetchOptions={({ ids, searchTerm }) => {
            if (ids?.length) {
              return Promise.resolve(formatOptions({ entries: [primaryFramework!], meta: { total: 1 } }));
            }

            return fetchSkillsFrameworksOptions({
              rank: SkillsFrameworkRank.Primary,
              availableInDemand: true,
              ...(searchTerm ? { term: searchTerm } : {})
            }).then(formatOptions);
          }}
          value={primaryFramework?.id}
          onChange={({ original }: Option<SkillsFramework>) => setPrimaryFramework(original)}
          onClear={primaryFramework ? () => setPrimaryFramework(undefined) : undefined}
          isFetchingSelectedOptions={false}
        />
        <SelectV2<SkillsFramework>
          multiple
          limit={3}
          label={t("sidePanel.secondarySkillsFramework")}
          fetchOptions={({ ids, searchTerm }) => {
            if (ids?.length) {
              return Promise.resolve(
                formatOptions({ entries: secondaryFrameworks, meta: { total: secondaryFrameworks.length } })
              );
            }

            return fetchSkillsFrameworksOptions({
              rank: SkillsFrameworkRank.Secondary,
              availableInDemand: true,
              ...(searchTerm ? { term: searchTerm } : {})
            }).then(formatOptions);
          }}
          value={map(secondaryFrameworks, "id")}
          onChange={(options: Option<SkillsFramework>[]) => setSecondaryFrameworks(map(options, "original"))}
          onClear={secondaryFrameworks.length ? () => setSecondaryFrameworks([]) : undefined}
          isFetchingSelectedOptions={false}
        />
        <Divider />
        {conflicts.length > 0 && (
          <Alert
            title={t("sidePanel.thereAreConflicts", { count: conflicts.length })}
            action={{
              text: t("sidePanel.review"),
              small: true,
              onClick: () => setConflictsSidePanelOpen(true)
            }}
          />
        )}
        <Typography variant="bodyBold">{t("sidePanel.attributesFromAllSkillsFrameworks")}</Typography>
        <AttributesBox
          skills={resolvedAttributes.skills}
          customFields={resolvedAttributes.customFields}
          template={template}
          subtemplate={subtemplate}
          activityCreator={activityCreator}
        />
      </Stack>
      {isConflictsSidePanelOpen && (
        <ConflictsReviewSidePanel
          conflicts={conflicts}
          onSave={handleConflictsResolve}
          onClose={() => setConflictsSidePanelOpen(false)}
        />
      )}
    </SidePanel>
  );
};
